import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FieldType } from '@ngx-formly/core';
import { catchError, map, of, startWith, switchMap, type Observable } from 'rxjs';

import { ModulesService } from '../../../api/services/modules.service';
import { composeEntriesFilter } from '../../../features/admin/modules/features/entries/entries-filter.util';
import { FormHelper } from '../../../features/admin/modules/features/form-builder/form.util';
import type { Entry } from '../../../features/admin/modules/modules.model';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { debugLog } from '../../utils/debug-logger';
import { fetchAllPages } from '../../utils/paging.util';
import { AutomaticChecksViewComponent } from './automatic-checks-view.component';
import type { AutomaticChecksPaths, RdwCheckState, RdwQuery } from './automatic-checks.types';
import {
  AUTOMATIC_CHECKS_DEFAULT_ASSETS_MODULE,
  buildAssetsFilter,
  buildKvkRow,
  buildNiwoRow,
  buildRdwRow,
  buildUninsuredLink,
  lastCheckFor,
  niwoConfigured,
  resolveCheckPaths,
  summarizeRdwChecks,
} from './automatic-checks.util';
import { liveEntrySourceOf } from './readonly/display-context.util';

/**
 * Assets read per request; `fetchAllPages` follows full pages, so a larger fleet is counted whole.
 */
const ASSETS_PAGE_SIZE = 1000;

/**
 * `automatic-checks`: read-only card with the KvK, RDW and (when the module defines the fields) NIWO
 * checks of a company entry.
 *
 * KvK and NIWO come from the entry's own values; RDW is fetched live from the assets that link to this
 * entry, because the counts are not stored on the company. Never writes its control.
 *
 * Reads the entry through `formState` (never `field.model` / the control), so the keyless clone the
 * Overzicht card mounts on the edit face renders the same thing (`CLONEABLE_OWN_VIEW_TYPES`).
 *
 * Always renders `bare`: on a detail page the Overzicht card or the tab section card already draws
 * the card and its heading, so a second one would nest a box in a box.
 * ponytail: no heading of its own, so it is meant to sit alone on a tab or in the Overzicht; placed
 * between other fields the rows are unlabeled. Add an opt-in title prop when a config needs that.
 *
 * Props (all optional): `assetsModuleCode` (default `assets`) and the config-only `checkPaths`
 * (partial `AutomaticChecksPaths` overrides).
 */
@Component({
  selector: 'formly-field-automatic-checks',
  imports: [AutomaticChecksViewComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<dd-automatic-checks-view [bare]="true" [rows]="rows()" />`,
})
export class FormlyAutomaticChecksComponent extends FieldType {
  /**
   * The API client, not `EntriesService.getEntries`: that one caches `assets` queries (nothing
   * invalidates them when the monthly backend check writes new dates) and leaves its per-module
   * loading flag stuck after an HTTP error.
   */
  readonly #modulesService = inject(ModulesService);
  readonly #modulesStore = inject(ModulesStore);

  /** Live formState from the detail page when there is one; the static snapshot otherwise. */
  readonly #formState = computed(
    () => liveEntrySourceOf(this.options?.formState)?.formState() ?? this.options?.formState
  );
  readonly #entry = computed(() => this.#formState()?.entry as Entry | undefined);
  readonly #paths = computed(() =>
    resolveCheckPaths(this.props['checkPaths'] as Partial<AutomaticChecksPaths> | undefined)
  );

  /** The company module's generated form; KvK (status colour) and NIWO (row gate) both look their field up in it. */
  readonly #generatedForm = computed(() => {
    const moduleId = this.#formState()?.moduleId ?? this.#entry()?.moduleId;
    return moduleId ? this.#modulesStore.generatedForms()[moduleId] : undefined;
  });

  /** Resolved so the KvK badge takes the status option's own colour (the one badge rule). */
  readonly #kvkStatusField = computed(() => {
    const generatedForm = this.#generatedForm();
    return generatedForm ? FormHelper.getFieldByKeyPath(`entry.${this.#paths().kvkStatus}`, generatedForm) : undefined;
  });

  /** The NIWO row is only shown on tenants whose company module carries the NIWO check fields. */
  readonly #niwoConfigured = computed(() => niwoConfigured(this.#generatedForm(), this.#paths()));

  readonly #rdwQuery = computed<RdwQuery | null>(
    () => {
      const companyId = this.#entry()?.id;
      if (!companyId) {
        return null;
      }
      const code =
        (this.props['assetsModuleCode'] as string | undefined)?.trim() || AUTOMATIC_CHECKS_DEFAULT_ASSETS_MODULE;
      return {
        moduleId: this.#modulesStore.enabledModules().find(module => module.code === code)?.id,
        filter: buildAssetsFilter(companyId, this.#paths()),
      };
    },
    // formState recomputes on every keystroke; only a different company or module refetches.
    { equal: (a, b) => a?.moduleId === b?.moduleId && a?.filter === b?.filter }
  );

  readonly #rdwState = toSignal(toObservable(this.#rdwQuery).pipe(switchMap(query => this.#loadRdw(query))), {
    initialValue: { status: 'loading' } as RdwCheckState,
  });

  /** The uninsured count links to the assets list, filtered the same way the count was fetched. */
  readonly #uninsuredLink = computed(() => {
    const companyId = this.#entry()?.id;
    const slug = this.#modulesStore.enabledModules().find(module => module.id === this.#rdwQuery()?.moduleId)?.slug;
    return companyId && slug ? buildUninsuredLink(slug, companyId, this.#paths()) : undefined;
  });

  readonly rows = computed(() => {
    const now = new Date();
    const entry = this.#entry()?.entry;
    return [
      buildKvkRow(entry, this.#paths(), now, this.#kvkStatusField(), this.#modulesStore),
      buildRdwRow(this.#rdwState(), now, this.#uninsuredLink()),
      ...(this.#niwoConfigured() ? [buildNiwoRow(entry, this.#paths(), now)] : []),
    ];
  });

  /**
   * Last-check date of the latest successful fetch, shown next to a later fetch error — keyed by
   * the query's filter (it names the company), so an error never shows another company's date.
   */
  #lastKnownCheck: { filter: string; date: string | undefined } | undefined;

  #loadRdw(query: RdwQuery | null): Observable<RdwCheckState> {
    if (!query) {
      return of({ status: 'idle' });
    }
    const moduleId = query.moduleId;
    if (!moduleId) {
      debugLog('AutomaticChecks', 'assets module not available', { code: this.props['assetsModuleCode'] });
      return of({ status: 'error' });
    }
    const loadPage = (skip: number) =>
      this.#modulesService.getModuleEntries({
        moduleId,
        // composeEntriesFilter adds the same archived base clause every entries list uses.
        $filter: composeEntriesFilter(query.filter),
        $top: String(ASSETS_PAGE_SIZE),
        $skip: String(skip),
        // A stable order, so the pages do not shift while they are walked.
        $orderBy: 'id',
      });
    return fetchAllPages(loadPage, ASSETS_PAGE_SIZE).pipe(
      map((entries): RdwCheckState => {
        const summary = summarizeRdwChecks(
          entries.map(asset => asset.entry),
          new Date(),
          this.#paths()
        );
        this.#lastKnownCheck = { filter: query.filter, date: summary.lastCheck };
        return { status: 'ready', summary };
      }),
      catchError((error: unknown) => {
        console.warn('[AutomaticChecks] RDW status could not be loaded', error);
        const forbidden = error instanceof HttpErrorResponse && error.status === 403;
        const lastCheck = lastCheckFor(this.#lastKnownCheck, query.filter);
        return of<RdwCheckState>({ status: 'error', forbidden, lastCheck });
      }),
      startWith<RdwCheckState>({ status: 'loading' })
    );
  }
}
